import { Group, Stack } from "@mantine/core";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormProvider, useForm } from "react-hook-form";
import { z } from "zod";
import { Modal } from "@/components/ui/Modal/Modal";
import { ControllerInput } from "@/components/ui/Input/ControllerInput";
import { Button } from "@/components/ui/Button/Button";

const duplicateFormSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, "Form name is required")
    .max(150, "Form name must be less than 150 characters"),
});

type DuplicateFormValues = z.infer<typeof duplicateFormSchema>;

interface DuplicateFormModalProps {
  sourceName: string;
  onClose: () => void;
  onSubmit: (name: string) => void;
  isSubmitting: boolean;
}

export function DuplicateFormModal({
  sourceName,
  onClose,
  onSubmit,
  isSubmitting,
}: DuplicateFormModalProps) {
  const methods = useForm<DuplicateFormValues>({
    resolver: zodResolver(duplicateFormSchema),
    defaultValues: { name: `${sourceName} (Copy)` },
  });

  const submit = methods.handleSubmit(({ name }) => onSubmit(name));

  return (
    <Modal isOpen onClose={onClose} title="Duplicate form" size="md" closeOnEscape>
      <FormProvider {...methods}>
        <form onSubmit={submit} noValidate>
          <Stack gap="var(--spacing-medium)">
            <ControllerInput name="name" label="New form name" required />
            <Group justify="flex-end">
              <Button variant="neutral-secondary" onClick={onClose} isDisabled={isSubmitting}>
                Cancel
              </Button>
              <Button variant="primary" type="submit" isDisabled={isSubmitting}>
                Duplicate
              </Button>
            </Group>
          </Stack>
        </form>
      </FormProvider>
    </Modal>
  );
}
